<template>
  <div class="location-geolocation-check pa-10">
    <title-page icon="mdi-map-marker-check">
      {{ $t('location.geolocation-check.title') }}
    </title-page>

    <v-row class="my-5">
      <v-col>
        <v-card>
          <v-card-text>
            <v-row align="center">
              <v-col>
                <p class="mb-0">{{ $t('location.geolocation-check.description') }}</p>
              </v-col>
              <v-col cols="auto">
                <v-checkbox
                    v-model="onlyUnchecked"
                    :label="$t('location.geolocation-check.only-unchecked')"
                    hide-details
                    class="mt-0 mr-4"
                    :disabled="scanning"
                />
              </v-col>
              <v-col cols="auto">
                <v-btn
                    color="primary"
                    small
                    :loading="scanning"
                    :disabled="scanning"
                    @click="startScan"
                >
                  <v-icon left>mdi-radar</v-icon>
                  {{ $t('location.geolocation-check.start-scan') }}
                </v-btn>
                <v-btn
                    v-if="scanning"
                    small
                    class="ml-2"
                    color="error"
                    @click="stopScan"
                >
                  <v-icon left>mdi-stop</v-icon>
                  {{ $t('stop') }}
                </v-btn>
              </v-col>
            </v-row>

            <template v-if="scanning || progress.total > 0">
              <v-row class="mt-4">
                <v-col>
                  <div class="d-flex align-center mb-2">
                    <span class="mr-3">{{ $t('location.geolocation-check.progress') }} :</span>
                    <strong>{{ progress.current }} / {{ progress.total }}</strong>
                    <span class="ml-2">({{ progressPercent }}%)</span>
                  </div>
                  <v-progress-linear
                      :value="progressPercent"
                      color="primary"
                      height="20"
                      rounded
                  >
                    <template v-slot:default>
                      {{ progressPercent }}%
                    </template>
                  </v-progress-linear>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="problematicLocations.length > 0 || scanCompleted">
      <v-col>
        <v-card>
          <v-card-title>
            {{ $t('location.geolocation-check.results') }}
            <v-chip class="ml-3" :color="problematicLocations.length > 0 ? 'error' : 'success'">
              {{ problematicLocations.length }} {{ $t('location.geolocation-check.problematic') }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <v-data-table
                :headers="headers"
                :items="problematicLocations"
                :items-per-page="20"
                :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
                class="elevation-1"
            >
              <template #item.name="{item}">
                <router-link :to="{name: 'location.detail', params: {id: item.id}}">
                  {{ item.name }}
                </router-link>
              </template>

              <template #item.address="{item}">
                <template v-if="item.address">
                  {{ item.address.number }} {{ item.address.street }}, {{ item.address.zipcode }} {{ item.address.city }}
                </template>
                <template v-else>-</template>
              </template>

              <template #item.distance="{item}">
                <v-chip :color="getDistanceColor(item.distance)" dark small>
                  {{ formatDistance(item.distance) }}
                </v-chip>
              </template>

              <template #item.issue="{item}">
                <v-chip :color="getIssueColor(item.issue)" dark small>
                  {{ $t('location.geolocation-check.issues.' + item.issue) }}
                </v-chip>
              </template>

              <template #item.actions="{item}">
                <v-btn
                    small
                    color="primary"
                    class="mr-2"
                    @click="openLocationModal(item)"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  {{ $t('edit') }}
                </v-btn>
                <v-btn
                    small
                    outlined
                    :href="getLocationUrl(item.id)"
                    target="_blank"
                >
                  <v-icon left small>mdi-open-in-new</v-icon>
                  {{ $t('view') }}
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <location-geo-fix-modal
        ref="locationModal"
        :location="selectedLocation"
        @update:location="onLocationUpdated"
    />
  </div>
</template>

<script>
import LocationRepository from "@repository/LocationRepository";
import LocationGeoFixModal from "@pages/Location/modal/LocationGeoFixModal.vue";
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'LocationGeolocationCheck',

  components: {
    LocationGeoFixModal,
  },

  data() {
    return {
      scanning: false,
      scanCompleted: false,
      stopRequested: false,
      progress: { current: 0, total: 0 },
      problematicLocations: [],
      selectedLocation: null,
      batchSize: 50,
      parallelCount: 5, // Nombre de traitements en parallèle
      distanceThreshold: 100, // mètres
      onlyUnchecked: true,
    }
  },

  computed: {
    progressPercent() {
      if (this.progress.total === 0) return 0;
      return Math.round((this.progress.current / this.progress.total) * 100);
    },

    headers() {
      return [
        { text: this.$t('name'), value: 'name' },
        { text: this.$t('address'), value: 'address', sortable: false },
        { text: this.$t('location.geolocation-check.distance'), value: 'distance' },
        { text: this.$t('location.geolocation-check.issue-type'), value: 'issue' },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '250px' },
      ];
    },
  },

  methods: {
    async startScan() {
      this.scanning = true;
      this.scanCompleted = false;
      this.stopRequested = false;
      this.problematicLocations = [];
      this.progress = { current: 0, total: 0 };

      try {
        // Récupérer le total
        const searchParams = {
          offset: 0,
          limit: 1,
          all: true,
          onlyUsable: true,
          onlyWithEvses: true,
          orderBy: 'createdAt',
          orderDirection: 'asc',
        };
        if (this.onlyUnchecked) {
          searchParams.coordinatesChecked = false;
        }

        const firstResult = await LocationRepository.search(searchParams);
        this.progress.total = firstResult.total;

        // Scanner par batch
        let offset = 0;
        while (offset < this.progress.total && !this.stopRequested) {
          const result = await LocationRepository.search({
            ...searchParams,
            offset,
            limit: this.batchSize,
          });

          // Traiter les locations en parallèle par groupes de parallelCount
          for (let i = 0; i < result.items.length && !this.stopRequested; i += this.parallelCount) {
            const chunk = result.items.slice(i, i + this.parallelCount);

            await Promise.all(
              chunk.map(location => this.processLocation(location))
            );

            this.progress.current += chunk.length;
          }

          offset += this.batchSize;
        }

        this.scanCompleted = true;
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.scanning = false;
      }
    },

    stopScan() {
      this.stopRequested = true;
    },

    async processLocation(location) {
      // Vérifier si l'emplacement a des coordonnées
      if (!location.coordinates?.latitude || !location.coordinates?.longitude) {
        this.problematicLocations.push({
          ...location,
          distance: null,
          issue: 'missing-coordinates',
          geocodedCoords: null,
        });
        return;
      }

      // Vérifier si l'emplacement a une adresse
      const addressQuery = this.buildAddressQuery(location);
      if (!addressQuery) {
        // Pas d'adresse, on ne peut pas vérifier
        return;
      }

      // Chaque traitement a son propre sessionId pour le parallélisme
      const sessionId = uuidv4();
      const instanceId = location.id; // Utiliser l'id de la location comme instanceId

      try {
        // Rechercher l'adresse
        const addresses = await LocationRepository.searchAddress(addressQuery, sessionId, instanceId);

        if (!addresses || addresses.length === 0) {
          this.problematicLocations.push({
            ...location,
            distance: null,
            issue: 'address-not-found',
            geocodedCoords: null,
          });
          return;
        }

        // Récupérer les détails du premier résultat
        const place = await LocationRepository.getPlace(addresses[0].placeId, sessionId);

        if (!place?.coordinates) {
          this.problematicLocations.push({
            ...location,
            distance: null,
            issue: 'geocoding-failed',
            geocodedCoords: null,
          });
          return;
        }

        // Calculer la distance
        const distance = this.calculateDistance(
            location.coordinates.latitude,
            location.coordinates.longitude,
            place.coordinates.lat,
            place.coordinates.lng
        );

        // Si distance > seuil, ajouter à la liste
        if (distance > this.distanceThreshold) {
          this.problematicLocations.push({
            ...location,
            distance,
            issue: 'wrong-position',
            geocodedCoords: {
              latitude: place.coordinates.lat,
              longitude: place.coordinates.lng,
            },
          });
        }
      } catch (error) {
        // En cas d'erreur, continuer avec le suivant
        console.error('Error processing location:', location.id, error);
      }
    },

    buildAddressQuery(location) {
      const parts = [];
      if (location.address?.number) parts.push(location.address.number);
      if (location.address?.street) parts.push(location.address.street);
      if (location.address?.city) parts.push(location.address.city);
      if (location.address?.zipcode) parts.push(location.address.zipcode);
      if (location.address?.country) parts.push(location.address.country);

      return parts.length >= 2 ? parts.join(' ') : null;
    },

    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000; // Rayon de la Terre en mètres
      const φ1 = lat1 * Math.PI / 180;
      const φ2 = lat2 * Math.PI / 180;
      const Δφ = (lat2 - lat1) * Math.PI / 180;
      const Δλ = (lon2 - lon1) * Math.PI / 180;

      const a = Math.sin(Δφ / 2) ** 2 +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c; // Distance en mètres
    },

    formatDistance(distance) {
      if (distance === null) return '-';
      if (distance >= 1000) {
        return (distance / 1000).toFixed(1) + ' km';
      }
      return Math.round(distance) + ' m';
    },

    getDistanceColor(distance) {
      if (distance === null) return 'grey';
      if (distance > 1000) return 'error';
      if (distance > 500) return 'warning';
      return 'orange';
    },

    getIssueColor(issue) {
      switch (issue) {
        case 'missing-coordinates':
          return 'error';
        case 'address-not-found':
          return 'warning';
        case 'geocoding-failed':
          return 'grey';
        case 'wrong-position':
          return 'orange';
        default:
          return 'grey';
      }
    },

    getLocationUrl(id) {
      const route = this.$router.resolve({ name: 'location.detail', params: { id } });
      return route.href;
    },

    openLocationModal(item) {
      this.selectedLocation = item;
      this.$nextTick(() => {
        this.$refs.locationModal.open();
      });
    },

    async onLocationUpdated(updatedLocation) {
      // Trouver l'index de l'emplacement dans la liste
      const index = this.problematicLocations.findIndex(l => l.id === updatedLocation.id);
      if (index === -1) return;

      // Re-scanner l'emplacement pour vérifier s'il est maintenant correct
      const item = this.problematicLocations[index];

      // Mettre à jour les données de l'emplacement
      item.coordinates = updatedLocation.coordinates;
      item.address = updatedLocation.address;

      // Re-vérifier la distance
      const addressQuery = this.buildAddressQuery(updatedLocation);
      if (!addressQuery) {
        // Pas d'adresse, retirer de la liste
        this.problematicLocations.splice(index, 1);
        return;
      }

      try {
        const sessionId = uuidv4();
        const addresses = await LocationRepository.searchAddress(addressQuery, sessionId, updatedLocation.id);

        if (!addresses || addresses.length === 0) {
          // Toujours problématique
          item.issue = 'address-not-found';
          item.distance = null;
          item.geocodedCoords = null;
          return;
        }

        const place = await LocationRepository.getPlace(addresses[0].placeId, sessionId);

        if (!place?.coordinates) {
          item.issue = 'geocoding-failed';
          item.distance = null;
          item.geocodedCoords = null;
          return;
        }

        const distance = this.calculateDistance(
            updatedLocation.coordinates.latitude,
            updatedLocation.coordinates.longitude,
            place.coordinates.lat,
            place.coordinates.lng
        );

        if (distance <= this.distanceThreshold) {
          // L'emplacement est maintenant correct, le retirer de la liste
          this.problematicLocations.splice(index, 1);
          this.$dialog.notify.success(this.$t('location.geolocation-check.corrected'));
        } else {
          // Toujours problématique, mettre à jour les infos
          item.distance = distance;
          item.geocodedCoords = {
            latitude: place.coordinates.lat,
            longitude: place.coordinates.lng,
          };
        }
      } catch (error) {
        console.error('Error re-checking location:', error);
      }
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },
}
</script>

<style lang="scss" scoped>
.location-geolocation-check {
  .v-data-table {
    a {
      text-decoration: none;
      color: #E53C22;
      font-weight: bold;
    }
  }
}
</style>
